import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { CMSHeroSection } from "../../../../services/cms";
import { resolveImageUrl } from "../../../../utils/imageFallback";

interface HeroSectionTabProps {
  heroSections: CMSHeroSection[];
  onEdit: (hero?: CMSHeroSection) => void;
  onDelete: (id: string) => void;
}

const HeroSectionTab: React.FC<HeroSectionTabProps> = ({
  heroSections,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          {t("admin.contentManagement.heroSections")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => onEdit()}
          sx={{
            background: "linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)",
            boxShadow: "0 2px 8px rgba(0,212,255,0.2)",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {t("admin.contentManagement.addHeroSection")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fill, minmax(320px, 1fr))",
            md: "repeat(2, 1fr)",
            xl: "repeat(3, 1fr)",
          },
          gap: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {heroSections.map((hero) => (
          <Card key={hero.id} sx={{ position: "relative" }}>
            {hero.background_image && (
              <Box
                sx={{
                  height: 150,
                  backgroundImage: `url(${resolveImageUrl(hero.background_image, '/placeholder-product.png')})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 100%)",
                  },
                }}
              />
            )}
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    {hero.title}
                  </Typography>
                  {hero.subtitle && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {hero.subtitle}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={
                    hero.is_active
                      ? t("admin.contentManagement.active")
                      : t("admin.contentManagement.inactive")
                  }
                  color={hero.is_active ? "success" : "default"}
                  size="small"
                />
              </Box>

              {hero.cta_text && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("admin.contentManagement.cta")}: {hero.cta_text}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title={t("admin.contentManagement.edit")}>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(hero)}
                    sx={{ color: "#00d4ff" }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("admin.contentManagement.delete")}>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(hero.id!)}
                    sx={{ color: "#ff3366" }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default HeroSectionTab;